// Retained audio for a browser capture (TT-1190; platform side TT-1101).
//
// The same 16 kHz mono s16le frames the capture sends to Transcribe, cut into
// 60 s WAV segments and PUT straight into the source-audio bucket on URLs
// appback presigns one segment at a time (`…/live-transcript/segment-url`).
// `finish()` uploads the partial last segment, then writes the set's close
// marker (`…/segments/close`) so retention compacts it.
//
// Each set is a server session: the tee asks for one when it is made
// (`…/segments/session`), and the server mints its epoch and caps how many a
// doc may have. Keys are `_segments/{doc_id}/{epoch}/{n:06d}.wav`, n restarts
// at 0 per epoch, and the close marks that epoch's set alone. A session that
// runs out rolls to a fresh one: at its segment cap the full set is closed
// first; past its time bound (410) it can't be, and is left to the stale
// sweep.
//
// Best effort by design: a segment that will not upload is retried a couple
// of times and then dropped with a warning. Nothing here throws into the
// capture or makes it wait. push() only keeps a reference to the frame; the
// WAV is assembled in the upload task, once a minute.

import AppBackService from "../../services/appback";
import { TARGET_SAMPLE_RATE } from "./pcmWorklet";

const BYTES_PER_SAMPLE = 2;
export const SEGMENT_SECONDS = 60;
export const SEGMENT_PCM_BYTES = SEGMENT_SECONDS * TARGET_SAMPLE_RATE * BYTES_PER_SAMPLE;
// Mirrors domain/livetap/host_audio.py MAX_SEGMENTS_PER_SESSION (a four-hour session is 240).
export const MAX_SEGMENTS_PER_SESSION = 300;
// Presign refusals that hold for every later segment of the doc, so the tee
// stops: 400 a bad epoch, 404 not a host doc.
const DOC_REFUSALS = new Set([400, 404]);
// The session's time bound (4 h 15 m from its epoch) has passed: roll.
const SESSION_EXPIRED = 410;
// At the session door: the doc has used up its sessions.
const SESSION_CAP = 429;
const UPLOAD_ATTEMPTS = 3;
// Segments waiting on a slow network; past this the newest is dropped rather
// than letting memory grow (each is ~1.9 MB).
const MAX_QUEUED = 3;
const WAV_HEADER_BYTES = 44;

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isClientRefusal = (status) => status >= 400 && status < 500 && status !== 429;

// A canonical 44-byte PCM WAV header followed by the frames, in order.
export function wavFromPcm(frames, pcmBytes) {
  const wav = new Uint8Array(WAV_HEADER_BYTES + pcmBytes);
  const view = new DataView(wav.buffer);
  const ascii = (offset, text) => [...text].forEach((ch, i) => view.setUint8(offset + i, ch.charCodeAt(0)));
  ascii(0, "RIFF");
  view.setUint32(4, 36 + pcmBytes, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, TARGET_SAMPLE_RATE, true);
  view.setUint32(28, TARGET_SAMPLE_RATE * BYTES_PER_SAMPLE, true); // byte rate
  view.setUint16(32, BYTES_PER_SAMPLE, true); // block align
  view.setUint16(34, 8 * BYTES_PER_SAMPLE, true); // bits per sample
  ascii(36, "data");
  view.setUint32(40, pcmBytes, true);
  let offset = WAV_HEADER_BYTES;
  frames.forEach((frame) => {
    wav.set(frame, offset);
    offset += frame.length;
  });
  return wav;
}

// `getBearer` returns the bearer the capture keeps fresh, for the keepalive
// close when the page goes away.
export function createSegmentTee({
  userId,
  docId,
  getBearer = () => null,
  segmentBytes = SEGMENT_PCM_BYTES,
  retryDelayMs = 1000,
  sleep = defaultSleep,
}) {
  let frames = [];
  let bytes = 0;
  let enabled = true;
  let finished = false;
  let queued = 0;
  let queue = Promise.resolve();
  // { epoch, next, landed, closed }: next is the next n, landed the segments PUT
  let session = null;
  // Full sessions rolled past whose close failed; finish() retries them.
  let unclosed = [];

  const tag = () => `[audio retention ${docId}${session ? `/${session.epoch}` : ""}]`;

  // Stop teeing for good: the doc keeps no audio, or refuses the tee.
  const disable = (reason) => {
    if (enabled) console.warn(`${tag()} not keeping audio: ${reason}`);
    enabled = false;
    frames = [];
    bytes = 0;
  };

  // A fresh server session, or null with the tee disabled. 5xx and network
  // failures are retried; a refusal (429, the doc's session cap, among them)
  // won't change.
  async function openSession() {
    for (let attempt = 1; attempt <= UPLOAD_ATTEMPTS; attempt += 1) {
      const res = await AppBackService.createSegmentSession(userId, docId).catch((error) => ({ error }));
      if (res && !res.error) {
        if (res.retain === false || !res.epoch) {
          disable("the document does not retain audio");
          return null;
        }
        session = { epoch: res.epoch, next: 0, landed: 0, closed: false };
        return session;
      }
      if (res?.status === SESSION_CAP) {
        disable("the document has no capture sessions left (429)");
        return null;
      }
      if (isClientRefusal(res?.status)) {
        disable(`no capture session (${res.status})`);
        return null;
      }
      if (attempt < UPLOAD_ATTEMPTS) await sleep(retryDelayMs * attempt);
    }
    disable(`no capture session after ${UPLOAD_ATTEMPTS} attempts`);
    return null;
  }

  // Asked for when the tee is made; the first upload waits on it.
  let opening = openSession();

  const roll = (reason) => {
    console.warn(`${tag()} ${reason}; starting a new session`);
    session = null;
    opening = openSession();
    return opening;
  };

  // The next n in the current session.
  const claimNumber = () => {
    const n = session.next;
    session.next += 1;
    return n;
  };

  // One segment: a fresh presign and a PUT per attempt, numbered in the
  // session it lands in. A 410 rolls the session once and starts the segment
  // over there; any other 4xx but 429 won't change on retry, so it drops just
  // this segment. 429, 5xx and network failures are retried.
  async function upload({ frames: segmentFrames, bytes: pcmBytes }) {
    await opening;
    if (!enabled || !session) return;
    if (session.next >= MAX_SEGMENTS_PER_SESSION) {
      // still inside its time bound, so the full set can be closed now
      const full = session;
      if (full.landed) {
        const res = await AppBackService.closeSegments(userId, docId, full.epoch).catch((error) => ({ error }));
        full.closed = !!res && !res.error;
        if (!full.closed) unclosed.push(full);
      }
      if (!(await roll(`the ${MAX_SEGMENTS_PER_SESSION}-segment session cap is reached`))) return;
    }
    const body = wavFromPcm(segmentFrames, pcmBytes);
    let rolled = false;
    let n = claimNumber();
    let attempt = 1;
    while (attempt <= UPLOAD_ATTEMPTS) {
      const presign = await AppBackService.createSegmentUrl(userId, docId, {
        epoch: session.epoch,
        n,
        bytes: body.length,
      });
      if (presign && !presign.error) {
        if (presign.retain === false) {
          disable("the document does not retain audio");
          return;
        }
        const put = await AppBackService.putPresigned(presign.url, body, presign.headers);
        if (put.ok) {
          session.landed += 1;
          return;
        }
      } else if (presign?.status === SESSION_EXPIRED && !rolled) {
        rolled = true;
        if (!(await roll("the session's time bound has passed (410)"))) return;
        n = claimNumber();
        attempt = 1; // the segment starts over in the new session
        continue;
      } else if (DOC_REFUSALS.has(presign?.status) || presign?.status === SESSION_EXPIRED) {
        disable(`segment ${n} refused (${presign.status})`);
        return;
      } else if (isClientRefusal(presign?.status)) {
        console.warn(`${tag()} segment ${n} refused (${presign.status}); dropped`);
        return;
      }
      if (attempt < UPLOAD_ATTEMPTS) await sleep(retryDelayMs * attempt);
      attempt += 1;
    }
    console.warn(`${tag()} segment ${n} did not upload after ${UPLOAD_ATTEMPTS} attempts; dropped`);
  }

  function cut() {
    if (!bytes) return;
    const segment = { frames, bytes };
    frames = [];
    bytes = 0;
    if (queued >= MAX_QUEUED) {
      console.warn(`${tag()} a segment was dropped: ${queued} still waiting to upload`);
      return;
    }
    queued += 1;
    queue = queue
      .then(() => upload(segment))
      .catch((err) => console.warn(`${tag()} a segment failed:`, err))
      .then(() => {
        queued -= 1;
      });
  }

  // Sessions with landed segments and no close yet: the rolled ones whose
  // close failed, then the current one.
  const needingClose = () => [...unclosed, session].filter((s) => s && s.landed && !s.closed);

  // The keepalive close: the page is going away, so it waits on nothing.
  const closeKeepalive = (bearer) => {
    needingClose().forEach((s) => {
      s.closed = true;
      AppBackService.closeSegmentsKeepalive(userId, docId, s.epoch, bearer || getBearer());
    });
  };

  // Close one session's set, retrying 5xx and network failures.
  async function closeSet(s) {
    for (let attempt = 1; attempt <= UPLOAD_ATTEMPTS; attempt += 1) {
      if (s.closed) return; // the page went away and closed it keepalive
      const res = await AppBackService.closeSegments(userId, docId, s.epoch).catch((err) => ({ error: err }));
      if (res && !res.error) {
        s.closed = true;
        return;
      }
      if (isClientRefusal(res?.status)) break;
      if (attempt < UPLOAD_ATTEMPTS) await sleep(retryDelayMs * attempt);
    }
    console.warn(`${tag()} could not close segment set ${s.epoch}; the stale sweep will compact it`);
  }

  return {
    get epoch() {
      return session?.epoch ?? null;
    },

    // Called with every PCM frame while the capture runs.
    push(frame) {
      if (!enabled || finished) return;
      frames.push(frame);
      bytes += frame.length;
      if (bytes >= segmentBytes) cut();
    },

    // The capture stopped: upload the partial segment, let the queue drain,
    // then close the set (and any rolled set whose close failed). The
    // capture has already dropped its own unload guards, so this keeps one of
    // its own until the closes are out: a tab closed meanwhile still closes
    // what landed. Never rejects.
    async finish() {
      if (finished) return;
      finished = true;
      if (enabled) cut();
      const onPageHide = () => closeKeepalive();
      window.addEventListener("pagehide", onPageHide);
      try {
        await queue;
        for (const s of needingClose()) await closeSet(s);
        unclosed = unclosed.filter((s) => !s.closed);
      } finally {
        window.removeEventListener("pagehide", onPageHide);
      }
    },

    // The page is going away: no time for a 1.9 MB upload, so the partial
    // segment is lost, but the close still goes out keepalive so retention
    // compacts what landed. Also covers a finish() still draining.
    closeOnUnload(bearer) {
      finished = true;
      enabled = false;
      closeKeepalive(bearer);
    },

    // The capture never got going: stop without closing anything.
    abandon() {
      finished = true;
      disable("the capture did not start");
    },
  };
}
